import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FolderIcon from '@mui/icons-material/Folder';
import ChatIcon from '@mui/icons-material/Chat';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DataObjectIcon from '@mui/icons-material/DataObject';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { api } from '../services/api';

const drawerWidth = 240;

const allMenuItems = [
  { text: 'Tableau de bord', icon: <DashboardIcon />, path: '/dashboard', permission: null },
  { text: 'Projets', icon: <FolderIcon />, path: '/projects', permission: 'projects:view_assigned' },
  { text: 'Communication', icon: <NotificationsIcon />, path: '/communication', permission: null },
  { text: 'Messages', icon: <ChatIcon />, path: '/chat', permission: 'chat:participate' },
  { text: 'Reporting Terrain', icon: <AssessmentIcon />, path: '/reporting', permission: 'reports:create' },
  { text: 'Collecte de Données', icon: <DataObjectIcon />, path: '/data-collection', permission: 'surveys:respond' },
  { text: 'Suivi des Sondages', icon: <AnalyticsIcon />, path: '/survey-analytics', permission: 'surveys:view_analytics' },
  { text: 'Inventaire & Ressources', icon: <InventoryIcon />, path: '/inventory', permission: 'inventory:scan' },
  { text: 'Base de Connaissances', icon: <MenuBookIcon />, path: '/knowledge-base', permission: 'knowledge:view_essential' },
  { text: 'Suivi des KPIs', icon: <AssessmentIcon />, path: '/kpi-tracking', permission: 'kpis:view_own' },
  { text: 'Gestion Utilisateurs', icon: <PeopleIcon />, path: '/users', permission: 'admin' },
];

const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { hasPermission, isAdmin, isAgence, hasSectionAccess } = usePermissions();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Mapping des chemins vers les IDs de sections
  const pathToSectionId: Record<string, string> = {
    '/dashboard': 'dashboard',
    '/projects': 'projects',
    '/reporting': 'reporting',
    '/data-collection': 'data_collection',
    '/communication': 'communication',
    '/chat': 'communication',
    '/inventory': 'inventory',
    '/knowledge-base': 'knowledge',
    '/kpi-tracking': 'kpis',
    '/users': 'users',
    '/collaborators': 'users',
  };

  // Filtrer le menu selon les permissions et les sections accessibles
  const menuItems = allMenuItems
    .filter(item => {
      // Vérifier l'accès à la section pour les collaborateurs
      const sectionId = pathToSectionId[item.path];
      if (sectionId && !hasSectionAccess(sectionId)) {
        return false;
      }
      
      if (!item.permission) return true; // Pas de permission requise
      if (item.permission === 'admin') return isAdmin || isAgence; // L'agence a aussi accès aux sections admin
      return hasPermission(item.permission as any);
    })
    .map(item => {
      // Pour les agents, remplacer "Communication" par "Notification" et utiliser l'icône de notification
      if (item.path === '/communication' && user?.role === 'agent_terrain') {
        return {
          ...item,
          text: 'Notification',
          icon: <NotificationsIcon />,
        };
      }
      return item;
    });
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Actualiser toutes les 30 secondes
    return () => clearInterval(interval);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await api.notifications.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          SOBEBRA-Zenith
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' }
            }}
          >
            Plateforme Collaborative BTL
          </Typography>
          <IconButton color="inherit" onClick={() => navigate('/communication')}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          <IconButton onClick={handleMenuOpen} sx={{ ml: 1 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem disabled>
              <AccountCircleIcon sx={{ mr: 1 }} />
              {user?.name} ({user?.role})
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate('/profile');
              }}
            >
              <AccountCircleIcon sx={{ mr: 1 }} />
              Mon profil
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1 }} />
              Déconnexion
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default Layout;

